/**
 * RoomShell, room route (/r/:id).
 *
 * Decides which room component to render based on WS state + room kind/phase.
 *
 * M3 routing logic (per §7 M3.T5):
 *   - kind=freeDraw → <FreeDrawRoom> (unchanged from M2)
 *   - kind=game, phase=lobby → <LobbyShell> (new M3)
 *   - kind=game, phase≠lobby → "Game starting..." stub (M4 fills in)
 *
 * M3.T6: For game rooms, show <JoinRoomForm> to collect player name + color
 * before establishing the room WS connection.
 *
 * The shell owns a SECOND WS connection specifically for the room:
 * it passes the sessionToken + roomId + name + color from the form to
 * startWsClient so the server registers the player with their chosen identity.
 *
 * Flow:
 *   1. Shell renders JoinRoomForm (identity=null, no WS yet).
 *   2. User submits form → setIdentity({name, color}).
 *   3. useEffect fires → startWsClient with name+color in HELLO.
 *   4. WELCOME + ROOM_STATE_SYNC received → roomState becomes "in-room".
 *   5. Route to FreeDrawRoom or LobbyShell based on roomKind.
 */

import { useRoute, useLocation } from "preact-iso";
import { useEffect, useRef, useState, useCallback } from "preact/hooks";
import { normalizeRoomCode } from "@drawlol/shared";
import type { ConnState, ClientControlFrame, WsClient } from "../ws.js";
import { startWsClient } from "../ws.js";
import { FreeDrawRoom } from "./FreeDrawRoom.js";
import { LobbyShell } from "./LobbyShell.js";
import { WordPickScreen, WaitingForDrawer } from "./WordPickScreen.js";
import { DrawingPhaseShell } from "./DrawingPhaseShell.js";
import { RevealPhaseShell } from "./RevealPhaseShell.js";
import { GameOverShell } from "./GameOverShell.js";
import { DrawingCanvas } from "../canvas/DrawingCanvas.js";
import { AudienceGuessSheet } from "../components/AudienceGuessSheet.js";
import { JoinRoomForm } from "../components/JoinRoomForm.js";
import { ColorReassignNotice } from "../components/ColorReassignNotice.js";
import { PaperBanner } from "../components/PaperBanner.js";
import { DrawerDisconnectBanner } from "../components/DrawerDisconnectBanner.js";
import { LeaveNoticeToast } from "../components/LeaveNoticeToast.js";
import { CorrectGuessToast } from "../components/CorrectGuessToast.js";
import { playSfx } from "../audio/sfx.js";
import {
  saveRoomSession,
  loadRoomSession,
  clearRoomSession,
  type SavedRoomSession,
} from "../session/roomSession.js";

type RoomShellProps = {
  /** Top-level WS connection state (from App). Carries sessionToken. */
  connState: ConnState;
  wsUrl: string;
  clientVersion: string;
};

type JoinIdentity = { name: string; color: string } | null;

export function RoomShell(props: RoomShellProps) {
  const { params } = useRoute();
  return <RoomShellContent key={normalizeRoomCode(params["id"] ?? "")} {...props} />;
}

function RoomShellContent({ connState, wsUrl, clientVersion }: RoomShellProps) {
  const { params } = useRoute();
  const { route } = useLocation();
  // V4.1.T5: room codes are case-insensitive. Canonicalize the URL param
  // (trim + uppercase) so a `/r/ab2c` link joins room AB2C, and so the code
  // we display + send in HELLO matches the server's canonical id.
  const roomId = normalizeRoomCode(params["id"] ?? "");

  const [roomState, setRoomState] = useState<ConnState>({ kind: "connecting" });
  /**
   * Q9 (audit 2026-06-11): saved per-tab session for THIS room, pinned after a fresh join. When set,
   * the join form is skipped and HELLO carries the saved token so the
   * server resumes the same player (same scores, no ghost row), a reload
   * used to mint a brand-new player. Cleared on room-gone / kicked /
   * auth-rejected (dead or revoked token).
   */
  const [resume, setResume] = useState<SavedRoomSession | null>(() => loadRoomSession(roomId));
  /**
   * M3.T6: identity from JoinRoomForm. null = not yet collected (show form).
   * Once set, the WS connection is started with name+color in HELLO.
   * Pre-seeded from the saved session on resume so the form is skipped.
   */
  const [identity, setIdentity] = useState<JoinIdentity>(() =>
    resume ? { name: resume.name, color: resume.color } : null,
  );

  /**
   * T17 (2a): colors already taken by current players in this room, fetched
   * from GET /api/room/:id so the join form can default to a random UNUSED
   * color. Best-effort: a failed/empty fetch just means the form defaults
   * over the full palette.
   */
  const [takenColors, setTakenColors] = useState<ReadonlyArray<string>>([]);
  useEffect(() => {
    if (!roomId || identity) return; // only needed while the form is shown
    let cancelled = false;
    fetch(`/api/room/${encodeURIComponent(roomId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { takenColors?: string[] } | null) => {
        if (!cancelled && Array.isArray(data?.takenColors)) setTakenColors(data.takenColors);
      })
      .catch(() => {
        /* best-effort; form defaults over the full palette */
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, identity]);

  /**
   * Live ref to the room WS client. Components below use `sendFrame` to
   * dispatch control frames (START_GAME, UPDATE_SETTINGS, KICK_PLAYER) over
   * the same socket that handed us our ROOM_STATE_SYNC. The ref is updated
   * in the useEffect that starts the connection so a stale send is impossible.
   */
  const wsClientRef = useRef<WsClient | null>(null);

  // Session token from the top-level WS connection
  const sessionToken =
    connState.kind === "connected" || connState.kind === "in-room" ? connState.sessionToken : null;

  const handleJoin = useCallback((name: string, color: string) => {
    setIdentity({ name, color });
  }, []);

  const sendFrame = useCallback((frame: ClientControlFrame): boolean => {
    const client = wsClientRef.current;
    if (!client) return false;
    return client.send(frame);
  }, []);

  /**
   * M4 batch 7 commit 2: binary stroke transport. Hands the drawer's
   * `<DrawingCanvas>` a way to ship STROKE_START / STROKE_BATCH /
   * STROKE_END frames to the server. Same socket as sendFrame; just
   * skips the JSON.stringify step.
   */
  const sendBinary = useCallback((buf: ArrayBuffer): boolean => {
    const client = wsClientRef.current;
    if (!client) return false;
    return client.sendBinary(buf);
  }, []);

  // Q9: a saved resume token joins immediately and stays pinned to it, a
  // later top-level token change must NOT restart the room socket (hence
  // joinToken in the deps, not sessionToken). A fresh join still waits for
  // the top-level session + the submitted form.
  const joinToken = resume?.sessionToken ?? sessionToken;

  // Q9 review fix (adversarial P1): kicked / room-gone are TERMINAL, the
  // join effect must never restart once they're set. Without this, the
  // cleanup below (setResume(null)) flips joinToken from the saved token to
  // the top-level one, the dep change restarted the socket, and a kicked
  // player who had resumed via sessionStorage silently rejoined the room.
  const isTerminal = roomState.kind === "kicked" || roomState.kind === "room-gone";

  useEffect(() => {
    if (isTerminal) return;
    if (!joinToken || !roomId || !identity) return;

    const client = startWsClient({
      url: wsUrl,
      clientVersion,
      onState: setRoomState,
      sessionToken: joinToken,
      roomId,
      playerName: identity.name,
      playerColor: identity.color,
    });
    wsClientRef.current = client;

    return () => {
      wsClientRef.current = null;
      client.close();
    };
  }, [wsUrl, clientVersion, joinToken, roomId, identity, isTerminal]);

  // Q9: persist the room session once joined so a reload resumes THIS
  // player. The token is the room socket's own (it survives the server's
  // session registry); the color saved is the server-ASSIGNED one so a
  // future fresh join after fallback keeps the color they actually had.
  const inRoomToken = roomState.kind === "in-room" ? roomState.sessionToken : null;
  useEffect(() => {
    if (!inRoomToken || !identity || roomState.kind !== "in-room") return;
    const assigned = roomState.players.find((p) => p.id === roomState.playerId)?.color;
    const saved = {
      sessionToken: inRoomToken,
      roomId,
      name: identity.name,
      color: assigned ?? identity.color,
    };
    saveRoomSession(saved);
    // Keep this room's player when the separate app connection gets a new token.
    setResume((previous) => previous ?? saved);
    // roomState is read for the assigned color but deliberately not a dep:
    // the assignment arrives in the same sync that delivers the token.
  }, [inRoomToken, roomId, identity]);

  // Q9: remember the name of everyone EVER seen in this room so players who
  // leave mid-game can still be named on the reveal / game-over standings
  // (the live roster drops them the moment they go). Append-only by design.
  const knownNamesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (roomState.kind !== "in-room") return;
    for (const p of roomState.players) knownNamesRef.current.set(p.id, p.name);
  }, [roomState]);

  // Q9: terminal rejections invalidate the saved session. room-gone /
  // kicked just clear it; auth-rejected (dead token, server restart or
  // eviction) also drops back to the join form so the user can rejoin
  // fresh without dead-ending on an error screen.
  useEffect(() => {
    if (roomState.kind === "room-gone" || roomState.kind === "kicked") {
      clearRoomSession();
      setResume(null);
    } else if (roomState.kind === "auth-rejected") {
      clearRoomSession();
      setResume(null);
      setIdentity(null);
    }
  }, [roomState.kind]);

  // M5.T8: fire the round-end swoosh once when phase transitions into
  // "reveal". The state is in-room only when we have a phase to compare;
  // for all other connection states, just remember the lack of phase.
  const currentPhase = roomState.kind === "in-room" ? roomState.phase : undefined;
  const prevPhaseRef = useRef<typeof currentPhase>(undefined);
  useEffect(() => {
    if (prevPhaseRef.current !== "reveal" && currentPhase === "reveal") {
      playSfx("roundSwoosh");
    }
    prevPhaseRef.current = currentPhase;
  }, [currentPhase]);

  // Show join form until identity is collected (awaiting user input).
  if (!identity) {
    return (
      <JoinRoomForm
        roomId={roomId}
        onJoin={handleJoin}
        takenColors={takenColors}
        onBack={() => route("/")}
      />
    );
  }

  // T17 (2a): if the server reassigned our color (our pick was taken), surface
  // it. The ASSIGNED color travels back in our SerializedPlayer via
  // ROOM_STATE_SYNC, so we compare it to the color we PICKED in the form.
  const assignedColor =
    roomState.kind === "in-room"
      ? roomState.players.find((p) => p.id === roomState.playerId)?.color
      : undefined;
  const colorNotice = (
    <ColorReassignNotice pickedColor={identity.color} assignedColor={assignedColor} />
  );

  if (roomState.kind === "connecting") {
    return (
      <div class="room-loading" role="status" aria-live="polite">
        <p>Joining room…</p>
      </div>
    );
  }

  if (roomState.kind === "room-gone") {
    return (
      <div class="room-error" role="alert">
        <p>No room with that code. Rooms vanish when everyone leaves.</p>
        <button class="scribble-btn" onClick={() => route("/")}>
          Back to home
        </button>
      </div>
    );
  }

  // Terminal kick (WS close 1008 "kicked"), auto-reconnect already stopped
  // in ws.ts; without that, ReconnectingWebSocket silently rejoined the room
  // (audit 2026-06-11). No "rejoin" affordance: the host said no.
  if (roomState.kind === "kicked") {
    return (
      <div class="room-error" role="alert" data-testid="room-kicked">
        <p>The host kicked you from this room.</p>
        <button class="scribble-btn" onClick={() => route("/")}>
          Back to home
        </button>
      </div>
    );
  }

  // Stale session token (server restarted, or the session was evicted).
  // Recovery: drop the cached identity so the join form renders again and
  // the next submit joins fresh with the top-level session.
  if (roomState.kind === "auth-rejected") {
    return (
      <div class="room-error" role="alert" data-testid="room-auth-rejected">
        <p>That session went stale. Join again and you're back in.</p>
        <button class="scribble-btn" onClick={() => setIdentity(null)}>
          Rejoin
        </button>
      </div>
    );
  }

  if (roomState.kind === "error" || roomState.kind === "reload-required") {
    // Q9 review fix (adversarial P2): ws.ts writes user-voiced messages for
    // specific failures (e.g. the rate-limit "give it a minute"), show
    // them. Only the two technical wire strings map to the generic copy;
    // telling a rate-limited user to reload was the opposite of the fix.
    const technical =
      roomState.kind === "error" &&
      (roomState.message === "connection failed" || roomState.message === "bad frame from server");
    return (
      <div class="room-error" role="alert">
        <p>
          {roomState.kind === "reload-required"
            ? "Server got an update — reload to catch up."
            : technical
              ? "Lost the connection. Reload and you're back in."
              : roomState.message}
        </p>
        <button class="scribble-btn" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }

  if (roomState.kind === "in-room") {
    const { roomKind, phase } = roomState;

    // Free Draw rooms → FreeDrawRoom (unchanged from M2). The color-reassign
    // notice rides above it for a just-joined player whose pick was taken.
    if (roomKind === "freeDraw") {
      return (
        <>
          {colorNotice}
          <FreeDrawRoom
            roomId={roomId}
            players={roomState.players}
            creatorId={roomState.creatorId}
            playerId={roomState.playerId}
            sendBinary={sendBinary}
            sendFrame={sendFrame}
          />
        </>
      );
    }

    // Game rooms
    if (roomKind === "game") {
      // Active-phase overlays (drawer-disconnect countdown + audience-leave
      // toast). Both come from `game` state populated by the M4 wire frames.
      // Lobby skips the overlay entirely (these events only fire during
      // wordPick/drawing/reveal per the reducer guard).
      const game = roomState.game;
      // Protocol 8 review fix (F1): PLAYER_LEFT now prunes the roster the
      // moment a socket closes: including the DISCONNECTED DRAWER during
      // their 10s grace window. Fall back to the known-names cache so the
      // grace banner says "Bob disconnected", not "The drawer".
      const lookupName = (id: string | undefined): string | null =>
        id
          ? (roomState.players.find((p) => p.id === id)?.name ??
            knownNamesRef.current.get(id) ??
            null)
          : null;
      // T17 (2a): every in-room game return rides the color-reassign notice so a
      // player who joins MID-GAME (landing straight in wordPick/drawing/reveal/
      // gameOver, never lobby) whose pick the server reassigned still sees which
      // color they got: the pick must never be SILENTLY overridden. The notice
      // self-hides (returns null) when assigned===picked, so always including it
      // is a no-op for the common path. Folded into `overlay` so each phase
      // branch carries it without duplicating the JSX.
      const overlay = (
        <>
          {colorNotice}
          {phase !== "lobby" && game ? (
            <>
              <div class="game-notices">
                {game.drawerDisconnect ? (
                  <DrawerDisconnectBanner
                    drawerName={lookupName(game.drawerDisconnect.drawerId) ?? "The drawer"}
                    graceUntil={game.drawerDisconnect.graceUntil}
                  />
                ) : null}
                {game.lastLeaveNotice ? (
                  <LeaveNoticeToast
                    displayName={game.lastLeaveNotice.displayName}
                    reason={game.lastLeaveNotice.reason}
                    at={game.lastLeaveNotice.at}
                  />
                ) : null}
              </div>
              {/* Audit 2026-06-11: the toast is gated to the DRAWING phase,
                  a turn-ending correct guess used to leave it sitting
                  dead-center OVER the reveal card, occluding the guesser's
                  own points row. At reveal, the card's beat-3 row IS the
                  celebration. */}
              {phase === "drawing" && (
                <CorrectGuessToast verdict={game.lastVerdict} word={game.currentTurn?.word} />
              )}
            </>
          ) : null}
        </>
      );

      if (phase === "lobby") {
        // Lobby phase → full lobby UI (M3). The color-reassign notice rides
        // above the lobby so a just-joined player whose pick was taken sees
        // which color they actually got (T17 2a).
        return (
          <>
            {colorNotice}
            <LobbyShell
              roomId={roomId}
              players={roomState.players}
              playerId={roomState.playerId}
              hostId={roomState.hostId}
              sendFrame={sendFrame}
              {...(roomState.lastAuthReject ? { authReject: roomState.lastAuthReject } : {})}
              {...(roomState.settings ? { serverSettings: roomState.settings } : {})}
            />
          </>
        );
      }
      // M4 batch 6a, wordPick phase: drawer sees WordPickScreen; everyone
      // else sees WaitingForDrawer. Drawer identity comes from the
      // server-projected `game.turnOrder[currentTurnIndex]` (the snapshot
      // redactor preserves these fields for all roles).
      if (phase === "wordPick") {
        const game = roomState.game;
        const drawerId = game?.turnOrder?.[game.currentTurnIndex];
        const isDrawer = drawerId !== undefined && drawerId === roomState.playerId;
        // Known-names fallback (F1): the drawer may already be pruned from
        // the roster during their disconnect-grace window.
        const drawerName =
          roomState.players.find((p) => p.id === drawerId)?.name ??
          (drawerId !== undefined ? knownNamesRef.current.get(drawerId) : undefined) ??
          "The drawer";
        if (isDrawer) {
          return (
            <>
              {overlay}
              <WordPickScreen
                wordChoices={game?.wordChoices}
                msRemaining={roomState.msRemaining}
                sendFrame={sendFrame}
                {...(game?.wordPickTimerSeconds !== undefined
                  ? { phaseTotalSeconds: game.wordPickTimerSeconds }
                  : {})}
              />
            </>
          );
        }
        return (
          <>
            {overlay}
            {game ? (
              <WaitingForDrawer
                drawerName={drawerName}
                drawerId={drawerId ?? ""}
                playerId={roomState.playerId}
                game={game}
                players={roomState.players}
                roomId={roomId}
                msRemaining={roomState.msRemaining}
                {...(game.wordPickTimerSeconds !== undefined
                  ? { phaseTotalSeconds: game.wordPickTimerSeconds }
                  : {})}
              />
            ) : (
              <div class="room-loading" role="status" aria-live="polite">
                <p>{drawerName} is picking…</p>
              </div>
            )}
          </>
        );
      }

      // M4 batch 6b, drawing phase: drawer sees canvas + Toolbar; audience
      // sees readOnly canvas + skeleton word + GuessInput; spectator sees
      // readOnly canvas + watching note.
      if (phase === "drawing") {
        const game = roomState.game;
        const isSpectator = roomState.spectatorIds.includes(roomState.playerId);
        if (game) {
          const isDrawer = game.currentTurn?.drawerId === roomState.playerId;
          const isAudienceCorrect =
            game.currentTurn?.correctGuesserIds.includes(roomState.playerId) ?? false;
          // Drawer + spectator don't see the input; audience-pending +
          // audience-correct do (audience-correct sees it disabled).
          const audienceCanGuess = !isDrawer && !isSpectator;
          return (
            <>
              {overlay}
              <DrawingPhaseShell
                playerId={roomState.playerId}
                game={game}
                players={roomState.players}
                roomId={roomState.roomId}
                isSpectator={isSpectator}
                msRemaining={roomState.msRemaining}
                {...(game.drawTimerSeconds !== undefined
                  ? { phaseTotalSeconds: game.drawTimerSeconds }
                  : {})}
                departedNames={knownNamesRef.current}
                sendBinary={sendBinary}
                sendFrame={sendFrame}
                guessInputSlot={
                  audienceCanGuess ? (
                    <AudienceGuessSheet
                      sendFrame={sendFrame}
                      disabled={isAudienceCorrect}
                      {...(game.lastVerdict ? { lastVerdict: game.lastVerdict } : {})}
                    />
                  ) : null
                }
              />
            </>
          );
        }
        // Game slice missing: render a brief loading placeholder.
        return (
          <>
            {overlay}
            <div class="drawing-phase-loading" role="status" aria-live="polite">
              <p>Starting the round…</p>
            </div>
          </>
        );
      }

      // M4 batch 6c, reveal phase: shows last TurnSummary + countdown
      // to the next turn (or gameOver). Same data for every recipient
      // (the canonical word is intentionally exposed in reveal, PRD
      // §3.9).
      if (phase === "reveal") {
        const game = roomState.game;
        if (game) {
          return (
            <>
              {overlay}
              <RevealPhaseShell
                game={game}
                players={roomState.players}
                playerId={roomState.playerId}
                roomId={roomState.roomId}
                msRemaining={roomState.msRemaining}
                departedNames={knownNamesRef.current}
                canvasSlot={
                  // V4.2.T13 (note 5h.1): the kept drawing renders UNDER the
                  // reveal card. <DrawingCanvas readOnly /> replays the persisted
                  // `$finalizedStrokes` (the T12 client store clears only on
                  // PHASE_CHANGED→wordPick, so the strokes survive into reveal).
                  // No sender props → inert. RevealPhaseShell owns the
                  // `.canvas-wrapper` (the same one the drawing phase uses) so the
                  // canvas stays the SAME size across the drawing → reveal swap.
                  <DrawingCanvas readOnly />
                }
              />
            </>
          );
        }
        return (
          <>
            {overlay}
            <div class="reveal-phase-loading" role="status" aria-live="polite">
              <p>Wrapping up the turn…</p>
            </div>
          </>
        );
      }

      // M4 batch 6c, gameOver phase: scoreboard + REMATCH button.
      if (phase === "gameOver") {
        const game = roomState.game;
        if (game) {
          return (
            <>
              {overlay}
              <GameOverShell
                game={game}
                players={roomState.players}
                roomId={roomId}
                playerId={roomState.playerId}
                sendFrame={sendFrame}
                onLeave={() => route("/")}
                departedNames={knownNamesRef.current}
              />
            </>
          );
        }
        return (
          <>
            {overlay}
            <div class="game-over-loading" role="status" aria-live="polite">
              <p>Loading final scores…</p>
            </div>
          </>
        );
      }

      // Any other unexpected phase falls through to the legacy stub.
      const isSpectator = roomState.spectatorIds.includes(roomState.playerId);
      return (
        <>
          {overlay}
          <div class="game-starting" data-phase={phase} role="status" aria-live="polite">
            {isSpectator && (
              <PaperBanner
                kind="info"
                message="You'll join the game next round."
                duration={Number.MAX_SAFE_INTEGER}
              />
            )}
            <p>Game starting…</p>
            {/* Audit 2026-06-11: "Phase: wordPick" was reducer jargon on a
                user-visible screen: the testid hook stays for e2e. */}
            <p class="game-phase-label" data-testid="phase-label" data-phase={phase} hidden></p>
          </div>
        </>
      );
    }
  }

  // Still connecting/waiting for ROOM_STATE_SYNC
  return (
    <div class="room-loading" role="status" aria-live="polite">
      <p>Connecting to room…</p>
    </div>
  );
}
